import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchMovies, searchStars } from '@/utils/media';

export default function SearchBox({ className = '' }: { className?: string }) {
  const [keyword, setKeyword] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const movieResults = useMemo(() => (keyword.trim() ? searchMovies(keyword).slice(0, 4) : []), [keyword]);
  const starResults = useMemo(() => (keyword.trim() ? searchStars(keyword).slice(0, 3) : []), [keyword]);

  const hasResults = movieResults.length > 0 || starResults.length > 0;

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const go = (path: string) => {
    setKeyword('');
    setOpen(false);
    navigate(path);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="flex items-center gap-2 rounded-full border border-background-200 bg-background-100 px-4 py-2 transition-colors focus-within:border-primary-400">
        <div className="flex h-4 w-4 items-center justify-center text-foreground-400">
          <i className="ri-search-line"></i>
        </div>
        <input
          value={keyword}
          onChange={(event) => {
            setKeyword(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && keyword.trim()) {
              setKeyword('');
              setOpen(false);
              navigate(`/category?q=${encodeURIComponent(keyword.trim())}`);
            }
          }}
          placeholder="搜索影视作品或明星资料"
          className="w-full min-w-0 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
        />
      </div>

      {open && keyword.trim() && (
        <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-50 max-h-[420px] overflow-y-auto rounded-lg border border-background-200 bg-background-50 p-2 shadow-lg">
          {hasResults ? (
            <>
              {movieResults.length > 0 && (
                <div className="mb-1">
                  <p className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-foreground-400">
                    影视作品
                  </p>
                  {movieResults.map((movie) => (
                    <button
                      key={movie.id}
                      type="button"
                      onClick={() => go(`/movie/${movie.id}`)}
                      className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition-colors hover:bg-background-100"
                    >
                      <div className="h-11 w-8 shrink-0 overflow-hidden rounded bg-background-200">
                        <img src={movie.cover} alt={movie.title} className="h-full w-full object-cover object-top" />
                      </div>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm text-foreground-900">{movie.title}</span>
                        <span className="block text-xs text-foreground-500">
                          {movie.type} · {movie.year}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {starResults.length > 0 && (
                <div>
                  <p className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-foreground-400">
                    明星资料
                  </p>
                  {starResults.map((star) => (
                    <button
                      key={star.id}
                      type="button"
                      onClick={() => go(`/star/${star.id}`)}
                      className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition-colors hover:bg-background-100"
                    >
                      <div className="h-9 w-9 shrink-0 overflow-hidden rounded-full bg-background-200">
                        <img src={star.avatar} alt={star.name} className="h-full w-full object-cover object-top" />
                      </div>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm text-foreground-900">{star.name}</span>
                        <span className="block text-xs text-foreground-500">{star.profession}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <p className="px-3 py-6 text-center text-sm text-foreground-500">
              未找到与“{keyword}”匹配的内容，试试其它关键词吧
            </p>
          )}
        </div>
      )}
    </div>
  );
}